// FILE: src/components/ShareModal.tsx
// PHOENIX PROTOCOL - SHARE MODAL V4.0 (DESIGN SYSTEM STANDARDIZED)
// STATUS: VERIFIED - COMPLETE FILE REPLACEMENT

import React from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Share2, Smartphone, MessageSquare } from 'lucide-react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  caseTitle: string;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, caseId, caseTitle }) => {
  if (!isOpen) return null;

  // Direct Portal URL
  const shareUrl = `${window.location.origin}/portal/${caseId}`;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    alert("Linku i Portalit u kopjua!");
  };

  const handleWhatsApp = () => {
    const text = `Përshëndetje, po ndaj me ju dosjen e rastit: ${caseTitle}. Shikojeni këtu: ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleViber = () => {
      const text = `Dosja: ${caseTitle} - ${shareUrl}`;
      window.open(`viber://forward?text=${encodeURIComponent(text)}`, '_blank');
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-canvas/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        onClick={onClose}
      >
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
          className="glass-panel w-full max-w-sm p-6"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-text-primary">Ndaj me Klientin</h3>
            <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={20}/></button>
          </div>

          <div className="space-y-3">
            {/* WhatsApp - Primary */}
            <button onClick={handleWhatsApp} className="w-full flex items-center justify-between p-4 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 rounded-xl group transition-all">
                <div className="flex items-center gap-3">
                    <Smartphone className="text-[#25D366]" />
                    <span className="font-bold text-[#25D366]">WhatsApp</span>
                </div>
                <Share2 size={16} className="text-[#25D366] opacity-50 group-hover:opacity-100" />
            </button>

            {/* Viber */}
            <button onClick={handleViber} className="w-full flex items-center justify-between p-4 bg-[#7360f2]/10 hover:bg-[#7360f2]/20 border border-[#7360f2]/30 rounded-xl group transition-all">
                <div className="flex items-center gap-3">
                    <MessageSquare className="text-[#7360f2]" />
                    <span className="font-bold text-[#7360f2]">Viber</span>
                </div>
                <Share2 size={16} className="text-[#7360f2] opacity-50 group-hover:opacity-100" />
            </button>

            {/* Copy Link - Fallback */}
            <button onClick={handleCopy} className="w-full flex items-center justify-between p-4 bg-surface hover:bg-hover border border-border-main rounded-xl group transition-all">
                <div className="flex items-center gap-3">
                    <Copy className="text-text-muted" />
                    <span className="font-medium text-text-secondary">Kopjo Linkun</span>
                </div>
                <div className="text-xs text-text-muted font-mono bg-surface/50 px-2 py-1 rounded max-w-[150px] truncate">
                    {shareUrl}
                </div>
            </button>
          </div>
          
          <div className="mt-6 text-center">
              <p className="text-xs text-text-muted">Ky link i jep klientit qasje të kufizuar (vetëm lexim) në dosje.</p>
          </div>

        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ShareModal;
